import { ActionTree, MutationTree } from "vuex";
import BulletinApi from "@/api/BulletinApi";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";
import i18n from "@/i18n";
import moment from "moment-timezone";
import { LocallySearchableAsset, IRootState } from "@iloq/shared";

const bulletinApi = new BulletinApi();

export interface IBulletinState {
  bulletin: any[];
  technical: any[];
  all: any[];
}

const initialState: IBulletinState = {
  bulletin: [],
  technical: [],
  all: [],
};

const actions: ActionTree<IRootState, IRootState> = {
  fetchBulletin({ commit, dispatch }, countryCode: string): Promise<any> {
    return bulletinApi
      .fetchBulletin(countryCode)
      .then((data: any) => {
        const res: [] = data;
        if (res && (res as any).indexOf("No bulletin found") >= 0) {
          commit("setBulletinResults", []);
        } else {
          commit("setBulletinResults", res);
          dispatch(
            "globalSearch/refreshData",
            {
              assetType: LocallySearchableAsset.bulletin,
              assets: (res as any[]).map((item) => ({
                ...item,
                wpDocType: LocallySearchableAsset.bulletin,
              })),
            },
            { root: true },
          );
        }
      })
      .catch(handleCommonApiErrors);
  },
};

const mutations: MutationTree<IBulletinState> = {
  setBulletinResults(state: IBulletinState, result: any): void {
    result.forEach((bulletinItem?: any) => {
      if (bulletinItem.bulletin_category_ids) {
        switch (bulletinItem.bulletin_category_ids[0]) {
          // Technical Updates
          case 2498:
          case 2502:
          case 2506:
          case 2510:
          case 2514:
          case 2518:
          case 2522:
          case 2526:
          case 2530:
          case 2534:
          case 2538:
          case 2542:
            bulletinItem.bulletin_category_slug = "technicalUpdates";
            break;
          // Heads-Up
          case 2495:
          case 2499:
          case 2503:
          case 2507:
          case 2511:
          case 2515:
          case 2519:
          case 2523:
          case 2527:
          case 2531:
          case 2535:
          case 2539:
            bulletinItem.bulletin_category_slug = "headsUp";
            break;
          // Local News
          case 2496:
          case 2500:
          case 2504:
          case 2508:
          case 2512:
          case 2516:
          case 2520:
          case 2524:
          case 2528:
          case 2532:
          case 2536:
          case 2540:
            bulletinItem.bulletin_category_slug = "localNews";
            break;
          // Events
          case 2497:
          case 2501:
          case 2505:
          case 2509:
          case 2513:
          case 2517:
          case 2521:
          case 2525:
          case 2529:
          case 2533:
          case 2537:
          case 2541:
            bulletinItem.bulletin_category_slug = "events";
            break;
          default:
            bulletinItem.bulletin_category_slug = "bulletin";
            break;
        }
      } else {
        bulletinItem.bulletin_category_slug = "bulletin";
      }

      // Inject h1 and/or meta to html content
      const metaCategory = `<span class="entry-meta__category">
          <span class="bulletin-category">
            ${i18n.t(`bulletin.category.${bulletinItem.bulletin_category_slug}`)}
          </span>
        </span>`;
      const metaDate = `<span class="entry-meta__date">
          <time class="entry-date" datetime="${new Date(bulletinItem.date).toISOString()}">
            ${moment(bulletinItem.date).format("D.M.YYYY")}
          </time>
        </span>`;
      const meta = `<p class="entry-meta">${metaCategory}${metaDate}</p>`;
      if (bulletinItem.content.indexOf("<h1") >= 0) {
        bulletinItem.content = bulletinItem.content
          .replace("<h1", '<h1 class="entry-title"') // Overrides later class definitions according to HTML5 spec
          .replace("</h1>", `</h1>${meta}`);
      } else {
        bulletinItem.content = `<h1 class="entry-title">${bulletinItem.title}</h1>${meta}${bulletinItem.content}`;
      }

      if (bulletinItem.bulletin_category_slug === "technicalUpdates") {
        state.technical.push(bulletinItem);
      } else {
        state.bulletin.push(bulletinItem);
      }
    });
    state.all = result;
  },
};

export const bulletinModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};
